import React, { useEffect, useState } from 'react'
import { capFraction, fmtMoney, fmtMoneyExact, fmtDate, stateMeta } from './format.js'

export function Spinner({ label = 'Loading' }) {
  return (
    <div className="flex items-center gap-2 p-6 text-neutral-500" role="status">
      <span className="h-3 w-3 animate-pulse rounded-full bg-brand-400" />
      {label}…
    </div>
  )
}

export function Banner({ tone = 'warn', children, onDismiss }) {
  const tones = {
    warn: 'border-warn/25 bg-warn/10 text-amber-800',
    over: 'border-over/25 bg-over/10 text-red-800',
    info: 'border-neutral-200 bg-white text-neutral-600',
  }
  return (
    <div className={`flex items-start gap-3 rounded-2xl border px-3.5 py-2.5 text-sm shadow-sm ${tones[tone]}`}>
      <div className="flex-1">{children}</div>
      {onDismiss && (
        <button onClick={onDismiss} className="shrink-0 opacity-50" aria-label="Dismiss">
          ✕
        </button>
      )}
    </div>
  )
}

/**
 * The one number the whole app exists to show. Sign is carried by the word as
 * well as the colour — "shortfall" is unambiguous where a minus sign at a
 * glance is not.
 */
/**
 * The headline number is what's actually happened — money spent so far
 * this month, not a forecast — since that's the number a glance should
 * trust most. The projected surplus/shortfall (still the thing that
 * matters for planning ahead) sits right underneath, smaller.
 */
export function SurplusHeadline({ household, categories = [], month }) {
  const spentSoFar = categories
    .filter((c) => c.kind !== 'excluded')
    .reduce((sum, c) => sum + Number(c.mtd ?? 0), 0)
  const surplus = Number(household.surplus_projected ?? 0)
  const positive = surplus >= 0
  return (
    <section className="rounded-3xl border border-neutral-200 bg-white p-5 shadow-sm">
      <div className="text-xs font-medium uppercase tracking-wide text-neutral-400">
        {month} · spent so far
      </div>
      <div className="mt-1 text-4xl font-semibold tracking-tight text-neutral-900">
        {fmtMoney(spentSoFar)}
      </div>
      <div className={`text-sm font-medium ${positive ? 'text-ok' : 'text-over'}`}>
        {fmtMoney(Math.abs(surplus))} projected {positive ? 'surplus' : 'shortfall'}
      </div>
      <dl className="mt-4 grid grid-cols-3 gap-3 rounded-2xl bg-neutral-50 p-3 text-sm">
        <Stat label="Take-home" value={fmtMoney(household.take_home)} />
        <Stat label="Fixed" value={fmtMoney(household.fixed_total)} />
        <Stat label="Discretionary" value={fmtMoney(household.disc_projected)} />
      </dl>
    </section>
  )
}

export function Stat({ label, value }) {
  return (
    <div>
      <dt className="text-xs text-neutral-400">{label}</dt>
      <dd className="font-medium tabular-nums text-neutral-900">{value}</dd>
    </div>
  )
}

/**
 * A category can be targeted (given a slider) whenever it's real spend that
 * a human might reasonably want to plan around — not the investment/transfer
 * kind that never counts as spend, and not the unreviewed catch-all, which
 * deliberately never gets a ceiling of its own.
 */
export function isTargetable(category) {
  return category.kind !== 'excluded' && category.category_id !== 'unreviewed'
}

export function CategoryRow({ category, onOpen, editMode, pendingCap, onChangeCap }) {
  const meta = stateMeta(category.state)
  // projected_eom is never less than mtd (see compute_state's `max(mtd, ...)`),
  // so the bar can show them as one solid segment (actually spent) plus a
  // lighter one on top of it (the forecast on top of that) — a fully solid
  // bar at the halfway mark used to mean "we're on pace to spend half the
  // cap," easy to misread as "we've already spent half of it."
  const actualFraction = capFraction(category.mtd, category.cap)
  const projectedFraction = capFraction(category.projected_eom, category.cap)

  return (
    <div className="rounded-2xl border border-neutral-200 bg-white p-4 shadow-sm">
      <button
        onClick={() => onOpen(category.category_id, category.label ?? category.category_id)}
        className="w-full text-left active:opacity-70"
      >
        <div className="flex items-baseline justify-between gap-3">
          <span className="font-medium text-neutral-900">{category.label ?? category.category_id}</span>
          <span className="tabular-nums text-sm text-neutral-600">
            {fmtMoney(category.mtd)}
            {category.cap ? <span className="text-neutral-400"> / {fmtMoney(category.cap)}</span> : null}
          </span>
        </div>

        {category.cap ? (
          <>
            <div className="relative mt-2 h-1.5 overflow-hidden rounded-full bg-neutral-100">
              <div
                className={`absolute inset-y-0 left-0 h-full rounded-full ${meta.bg} opacity-30`}
                style={{ width: `${Math.min(100, projectedFraction * 100)}%` }}
              />
              <div
                className={`absolute inset-y-0 left-0 h-full rounded-full ${meta.bg}`}
                style={{ width: `${Math.min(100, actualFraction * 100)}%` }}
              />
            </div>
            <div className="mt-1 flex justify-between text-xs">
              <span className={`font-medium ${meta.text}`}>
                {meta.label}
                {category.state === 'red' || category.state === 'orange'
                  ? ` · ${fmtMoney(category.projected_eom)} projected`
                  : ''}
              </span>
              <span className="text-neutral-400">{category.txn_count} txns</span>
            </div>
          </>
        ) : (
          <div className="mt-1 text-xs text-neutral-400">no cap · {category.txn_count} txns</div>
        )}
      </button>

      {editMode && isTargetable(category) ? (
        <div className="mt-2 border-t border-neutral-100 pt-2">
          <TargetEditor
            categoryId={category.category_id}
            cap={category.cap}
            fallback={category.projected_eom}
            pendingValue={pendingCap}
            onChange={onChangeCap}
          />
        </div>
      ) : null}
    </div>
  )
}

/**
 * A shared "set a target" field — used on the compact category row and the
 * full category screen, and for categories that have no cap yet at all (a
 * fixed bill like the mortgage, normally projected from history, which can
 * still be wrong). It only stages a change locally via `onChange`; nothing
 * is written until the planning bar's Save is pressed, since the whole point
 * of the toggle is to try numbers freely before committing any of them.
 *
 * A plain number field rather than a slider: a slider is hard to land on an
 * exact number, and one stray drag stages a change nobody meant to make.
 * Typing is also how you'd actually explore "what if we cut this to
 * nothing" — 0 is a legitimate target, just not a negative one.
 */
export function TargetEditor({ categoryId, cap, fallback, pendingValue, onChange, compact }) {
  const base = cap ?? fallback ?? 100
  const [text, setText] = useState(String(pendingValue ?? base))

  // Follow the parent's value when it changes from outside this input —
  // Discard clearing the stage, Save resolving to the new saved cap, or a
  // fresh fetch replacing `cap`/`fallback` entirely.
  useEffect(() => {
    setText(String(pendingValue ?? base))
  }, [pendingValue, base])

  const modified = pendingValue != null && Number(pendingValue) !== Number(base)

  function handleChange(e) {
    const raw = e.target.value
    setText(raw)
    const num = Number(raw)
    if (raw.trim() !== '' && Number.isFinite(num) && num >= 0) {
      onChange(categoryId, num)
    }
  }

  return (
    <div onClick={(e) => e.stopPropagation()} className="mt-1">
      <div className="flex items-center justify-between text-xs text-neutral-500">
        <span>{cap != null ? 'cap' : 'target'}</span>
        {modified && <span className="font-medium text-brand-600">modified</span>}
      </div>
      <div
        className={`mt-1 flex items-center gap-1 rounded-lg border px-2 py-1 ${
          modified ? 'border-brand-300' : 'border-neutral-200'
        }`}
      >
        <span className="text-xs text-neutral-400">$</span>
        <input
          type="text"
          inputMode="decimal"
          value={text}
          onChange={handleChange}
          aria-label={cap != null ? 'Cap amount' : 'Target amount'}
          className="w-full bg-transparent text-sm tabular-nums text-neutral-800
                     focus:outline-none"
        />
      </div>
      {cap == null && !compact && (
        <div className="mt-0.5 text-[11px] text-neutral-400">
          no cap today — projecting {fmtMoney(fallback)} from history
        </div>
      )}
    </div>
  )
}

/**
 * Plan mode's dense view: every targetable category in one flat grid, sized
 * for setting numbers rather than monitoring status — so it drops the
 * progress bar, state badge and transaction count that the read-only row
 * carries, and fits many more categories on screen at once.
 */
export function PlanCard({ category, pendingValue, onChangeCap }) {
  return (
    <div className="rounded-2xl border border-neutral-200 bg-white p-3 shadow-sm">
      <div className="truncate text-xs font-medium text-neutral-700">
        {category.label ?? category.category_id}
      </div>
      <TargetEditor
        categoryId={category.category_id}
        cap={category.cap}
        fallback={category.projected_eom}
        pendingValue={pendingValue}
        onChange={onChangeCap}
        compact
      />
    </div>
  )
}

export function PlanGrid({ categories, pendingCaps, onChangeCap }) {
  const targetable = categories.filter(isTargetable)
  if (!targetable.length) return null
  return (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
      {targetable.map((c) => (
        <PlanCard
          key={c.category_id}
          category={c}
          pendingValue={pendingCaps[c.category_id]}
          onChangeCap={onChangeCap}
        />
      ))}
    </div>
  )
}

const NEW_EVENT = '__new__'

export function TransactionRow({
  txn, onFlag, onOpenRule, onSplit, onDeleteSplit, onMarkOnetime, onUnmarkOnetime,
  onDismissLarge, flagging, splitting, markingOnetime, dismissingLarge,
  eventNames = [], onNeedEventNames, categories = [], onNeedCategories,
  showSplit = true, showFlag = true, showOnetime = true, categoryLabel, eventOnly = false,
  categorizeMode = false,
}) {
  const [splitOpen, setSplitOpen] = useState(false)
  const [amount, setAmount] = useState('')
  const [onetimeOpen, setOnetimeOpen] = useState(false)
  const [selectedEvent, setSelectedEvent] = useState('')
  const [newEventName, setNewEventName] = useState('')
  const [flagOpen, setFlagOpen] = useState(false)
  // Pre-filled from the audit's suggestion, if it left one — a human still
  // has to hit Categorize to actually accept it, this just saves the tap
  // of picking the same thing from the dropdown.
  const [flagCategory, setFlagCategory] = useState(txn.suggestion?.category_id ?? '')
  const [flagNote, setFlagNote] = useState('')
  const splits = txn.splits || []

  function openOnetimeForm() {
    onNeedEventNames?.()
    setOnetimeOpen(!onetimeOpen)
  }

  function openFlagForm() {
    onNeedCategories?.()
    setFlagOpen(!flagOpen)
  }

  const canSubmitFlag = !categorizeMode || Boolean(flagCategory)

  function submitFlag(e) {
    e.preventDefault()
    if (!canSubmitFlag) return
    onFlag(txn, flagCategory || undefined, flagNote.trim() || undefined)
    setFlagCategory('')
    setFlagNote('')
    setFlagOpen(false)
  }

  function acceptSuggestion() {
    onFlag(txn, txn.suggestion.category_id, txn.suggestion.reason)
  }

  function submitSplit(e) {
    e.preventDefault()
    const num = Number(amount)
    if (!Number.isFinite(num) || num <= 0) return
    onSplit(txn, num)
    setAmount('')
    setSplitOpen(false)
  }

  const pendingEvent = selectedEvent === NEW_EVENT ? newEventName.trim() : selectedEvent
  const canSubmitOnetime = !eventOnly || Boolean(pendingEvent)

  function submitOnetime(e) {
    e.preventDefault()
    if (!canSubmitOnetime) return
    onMarkOnetime(txn, pendingEvent)
    setSelectedEvent('')
    setNewEventName('')
    setOnetimeOpen(false)
  }

  return (
    <li className="border-b border-neutral-100 py-3 last:border-b-0">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-2">
            <span className="truncate font-medium text-neutral-900">{txn.merchant || '(no merchant)'}</span>
            <span className="shrink-0 tabular-nums text-neutral-900">
              {fmtMoneyExact(txn.spend_amount || txn.amount)}
            </span>
          </div>
          <div className="truncate text-xs text-neutral-400">{txn.original_statement}</div>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-neutral-500">
            <span>{fmtDate(txn.date)}</span>
            <span>·</span>
            <span className="truncate">{txn.account}</span>
            {categoryLabel && (
              <>
                <span>·</span>
                <span className="truncate">{categoryLabel}</span>
              </>
            )}
            <span>·</span>
            <button
              onClick={() => onOpenRule(txn.rule_id)}
              className="rounded-full bg-neutral-100 px-1.5 py-0.5 font-mono text-[11px] text-neutral-500"
            >
              {txn.rule_id}
            </button>
            {txn.pending && <span className="font-medium text-neutral-400">pending</span>}
            {txn.flagged && <span className="font-medium text-warn">flagged</span>}
            {txn.large && !txn.onetime && <span className="font-medium text-warn">large purchase</span>}
            {txn.onetime && (
              <span className="font-medium text-brand-600">
                one-time{txn.onetime.event ? ` · ${txn.onetime.event}` : ''}
              </span>
            )}
            {txn.reimbursed_amount > 0 && (
              <span className="font-medium text-brand-600">
                −{fmtMoneyExact(txn.reimbursed_amount)} reimbursed
              </span>
            )}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          {(showOnetime || txn.onetime) && (
            <button
              onClick={() => (txn.onetime ? onUnmarkOnetime(txn.onetime.id) : openOnetimeForm())}
              disabled={markingOnetime}
              className="rounded-full border border-neutral-200 px-2.5 py-1 text-xs
                         font-medium text-neutral-500 disabled:opacity-40"
              aria-label={
                txn.onetime
                  ? 'Unmark as one-time'
                  : eventOnly
                  ? 'Assign to an event'
                  : "Mark as one-time — won't recur this month"
              }
            >
              {txn.onetime ? '✓ One-time' : eventOnly ? 'Assign to event' : 'One-time'}
            </button>
          )}
          {onDismissLarge && !txn.onetime && (
            <button
              onClick={() => onDismissLarge(txn)}
              disabled={dismissingLarge}
              className="rounded-full border border-neutral-200 px-2.5 py-1 text-xs
                         font-medium text-neutral-500 disabled:opacity-40"
              aria-label="Not a one-time event"
            >
              Not an event
            </button>
          )}
          {showSplit && (
            <button
              onClick={() => setSplitOpen(!splitOpen)}
              disabled={splitting}
              className="rounded-full border border-neutral-200 px-2.5 py-1 text-xs
                         font-medium text-neutral-500 disabled:opacity-40"
              aria-label="Split this transaction"
            >
              Split
            </button>
          )}
          {showFlag && (
            <button
              onClick={openFlagForm}
              disabled={flagging}
              className="rounded-full border border-neutral-200 px-2.5 py-1 text-xs
                         font-medium text-neutral-500 disabled:opacity-40"
              aria-label={
                categorizeMode
                  ? 'Categorize this transaction'
                  : txn.flagged
                  ? 'Already flagged — add a note or recategorize'
                  : 'Flag or recategorize this transaction'
              }
            >
              {categorizeMode ? 'Categorize' : txn.flagged ? '✓ Flag' : 'Flag'}
            </button>
          )}
        </div>
      </div>

      {categorizeMode && txn.suggestion && !flagOpen && (
        <div className="mt-2 flex items-center justify-between gap-2 rounded-lg bg-brand-50 px-2.5 py-1.5">
          <p className="text-xs text-brand-700">
            Suggested: <span className="font-medium">{txn.suggestion.category_label}</span>
            {txn.suggestion.reason ? ` — ${txn.suggestion.reason}` : ''}
          </p>
          <button
            onClick={acceptSuggestion}
            disabled={flagging}
            className="shrink-0 rounded-full bg-brand-600 px-2.5 py-1 text-xs font-medium text-white
                       disabled:opacity-40"
          >
            Accept
          </button>
        </div>
      )}

      {splits.length > 0 && (
        <ul className="mt-2 space-y-1 pl-0.5">
          {splits.map((s) => (
            <li key={s.id} className="flex items-center justify-between gap-2 text-xs text-neutral-500">
              <span className="truncate">
                −{fmtMoneyExact(s.reimbursed_amount)}
                {s.note ? ` · ${s.note}` : ''}
              </span>
              <button
                onClick={() => onDeleteSplit(s.id)}
                disabled={splitting}
                className="shrink-0 text-neutral-400 underline disabled:opacity-40"
              >
                remove
              </button>
            </li>
          ))}
        </ul>
      )}

      {splitOpen && (
        <form onSubmit={submitSplit} className="mt-2 flex items-center gap-2">
          <span className="text-xs text-neutral-400">Reimbursed</span>
          <input
            type="number"
            inputMode="decimal"
            step="0.01"
            min="0"
            autoFocus
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="w-24 rounded-lg border border-neutral-200 px-2 py-1 text-sm tabular-nums"
            placeholder="0.00"
          />
          <button
            type="submit"
            disabled={splitting}
            className="rounded-full bg-brand-600 px-2.5 py-1 text-xs font-medium text-white disabled:opacity-40"
          >
            Save
          </button>
        </form>
      )}

      {onetimeOpen && (
        <form onSubmit={submitOnetime} className="mt-2 flex flex-wrap items-center gap-2">
          <span className="text-xs text-neutral-400">Event</span>
          <select
            value={selectedEvent}
            onChange={(e) => setSelectedEvent(e.target.value)}
            autoFocus
            className="rounded-lg border border-neutral-200 px-2 py-1 text-sm text-neutral-700"
          >
            {!eventOnly && <option value="">No event</option>}
            {eventOnly && !selectedEvent && <option value="" disabled>Choose an event…</option>}
            {eventNames.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
            <option value={NEW_EVENT}>+ New event…</option>
          </select>
          {selectedEvent === NEW_EVENT && (
            <input
              value={newEventName}
              onChange={(e) => setNewEventName(e.target.value)}
              placeholder="Event name"
              autoFocus
              className="w-32 rounded-lg border border-neutral-200 px-2 py-1 text-sm"
            />
          )}
          <button
            type="submit"
            disabled={markingOnetime || !canSubmitOnetime}
            className="rounded-full bg-brand-600 px-2.5 py-1 text-xs font-medium text-white disabled:opacity-40"
          >
            Save
          </button>
        </form>
      )}

      {flagOpen && (
        <form onSubmit={submitFlag} className="mt-2 flex flex-wrap items-center gap-2">
          <span className="text-xs text-neutral-400">Category</span>
          <select
            value={flagCategory}
            onChange={(e) => setFlagCategory(e.target.value)}
            autoFocus
            className="rounded-lg border border-neutral-200 px-2 py-1 text-sm text-neutral-700"
          >
            {!categorizeMode && <option value="">No change</option>}
            {categorizeMode && !flagCategory && <option value="" disabled>Choose a category…</option>}
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label ?? c.id}
              </option>
            ))}
          </select>
          <input
            value={flagNote}
            onChange={(e) => setFlagNote(e.target.value)}
            placeholder={
              categorizeMode
                ? 'Why? (helps the audit write a rule)'
                : flagCategory
                ? 'Why? (helps the audit write a rule)'
                : 'Note (optional)'
            }
            className="min-w-[10rem] flex-1 rounded-lg border border-neutral-200 px-2 py-1 text-sm"
          />
          <button
            type="submit"
            disabled={flagging || !canSubmitFlag}
            className="rounded-full bg-brand-600 px-2.5 py-1 text-xs font-medium text-white disabled:opacity-40"
          >
            {categorizeMode ? 'Categorize' : flagCategory ? 'Recategorize' : 'Flag'}
          </button>
        </form>
      )}
    </li>
  )
}

/**
 * Prior months at a glance. Bars rather than a line: twelve points on a phone
 * is too few for a line to say anything a bar chart doesn't say more plainly,
 * and bars degrade gracefully to two or three months of history.
 */
export function Sparkline({ history, cap }) {
  if (!history?.length) return null
  const max = Math.max(...history.map((h) => Number(h.mtd ?? 0)), Number(cap ?? 0), 1)

  return (
    <div className="flex h-16 items-end gap-1" aria-label="Monthly history">
      {history.map((h) => {
        const value = Number(h.mtd ?? 0)
        const over = cap && value > Number(cap)
        return (
          <div key={h.month} className="flex flex-1 flex-col items-center gap-1">
            <div
              className={`w-full rounded-t-md ${over ? 'bg-over' : 'bg-brand-200'}`}
              style={{ height: `${Math.max(2, (value / max) * 52)}px` }}
              title={`${h.month}: ${fmtMoney(value)}`}
            />
            <span className="text-[9px] text-neutral-400">{h.month.slice(5)}</span>
          </div>
        )
      })}
    </div>
  )
}
